import * as React from 'react';
import { HTMLAttributes, ReactNode, useEffect, useMemo, useState } from 'react';

import { PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';

import { cn } from '../../utils';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Columns2Icon,
  EllipsisIcon,
  Loader2Icon,
} from 'lucide-react';
import { Button } from './button';
import { Card } from './card';
import { Checkbox } from './checkbox';
import { Popover } from './popover';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b transition-colors data-[state=selected]:bg-muted',
      className
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-12 sm:text-sm text-xs sm:px-4 px-1 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0',
      className
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'px-1 py-2 text-xs sm:p-4 sm:text-sm align-middle [&:has([role=checkbox])]:pr-0',
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export interface AdvancedTableProps<T extends { id: React.Key }>
  extends HTMLAttributes<HTMLTableElement> {
  pageSize?: number;
  entries: T[];
  columns: ({
    key: string;
    name: ReactNode;
    cell: (entry: T) => ReactNode;
    sortValue?: (entry: T) => number | string | boolean;
    alwaysVisible?: boolean;
  } | null)[];
  showPaginationButtons?: boolean;
  showHeader?: boolean;
  asyncPagination?: {
    page: number;
    setPage: (page: number) => void;
    loading: boolean;
    pageCount: number;
    pageSize: number;
    sortedColumn?: {
      sortable: string[];
      key?: string;
      sortType?: -1 | 1;
      onChange: (key: string, sortType: 1 | -1) => void;
    };
  };
  isColumnVisibilityEnabled?: boolean;
  onRowClick?: (entry: T) => void;
}

export const AdvancedTable = <T extends { id: React.Key }>({
  className,
  pageSize = 10,
  entries,
  columns,
  showPaginationButtons = true,
  showHeader = true,
  isColumnVisibilityEnabled = false,
  children,
  asyncPagination,
  onRowClick,
  ...props
}: AdvancedTableProps<T>) => {
  const [activeSortValue, setActiveSortValue] =
    useState<(entry: T) => number | string | boolean>();
  const [sortType, setSortType] = useState(0);
  const [hiddenColumnsKeys, setHiddenColumnsKeys] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const cols = useMemo(
    () => columns.filter((col) => col !== null),
    [columns]
  ) as {
    key: string;
    name: ReactNode;
    cell: (entry: T) => ReactNode;
    sortValue?: (entry: T) => number | string | boolean;
    alwaysVisible?: boolean;
  }[];

  const entriesToRender = useMemo(() => {
    if (asyncPagination) {
      return entries;
    }

    let ents = entries;
    if (sortType && activeSortValue) {
      ents = ents
        .slice()
        .sort((a, b) =>
          activeSortValue(a) < activeSortValue(b)
            ? -sortType
            : activeSortValue(a) > activeSortValue(b)
            ? sortType
            : 0
        );
    }
    if (pageSize && entries.length > pageSize) {
      ents = ents.slice(pageSize * page, pageSize * (page + 1));
    }

    return ents;
  }, [entries, sortType, activeSortValue, page, pageSize]);

  if (!entries.length) {
    return (
      <div className="border p-4 rounded-xl text-sm text-center text-tertiary">
        No data to display
      </div>
    );
  }

  return (
    <div className={`border pb-4 rounded-xl relative ${className}`}>
      {asyncPagination?.loading && (
        <div className="bg-background/70 h-full w-full absolute z-10 flex items-center justify-center">
          <Loader2Icon className="animate-spin h-10 w-10 text-primary" />
        </div>
      )}
      <Table {...props}>
        {showHeader && (
          <TableHeader>
            <TableRow>
              {cols.map(
                ({ name, key, sortValue }) =>
                  key !== 'actions' &&
                  !hiddenColumnsKeys.includes(key) && (
                    <TableHead
                      className={`first:pl-4 last:pr-4 ${
                        (asyncPagination
                          ? asyncPagination.sortedColumn?.sortable.includes(key)
                          : sortValue) &&
                        'cursor-pointer flex gap-2 items-center select-none'
                      }`}
                      key={key}
                      onClick={
                        asyncPagination?.sortedColumn
                          ? () => {
                              const {
                                key: activeKey,
                                sortType,
                                onChange,
                              } = asyncPagination!.sortedColumn!;
                              if (activeKey !== key) {
                                onChange(key, -1);
                              } else {
                                onChange(key, sortType === 1 ? -1 : 1);
                              }
                            }
                          : sortValue
                          ? () => {
                              if (sortValue !== activeSortValue) {
                                setActiveSortValue(() => sortValue);
                                setSortType(-1);
                              } else if (sortType === -1) {
                                setSortType(1);
                              } else if (sortType === 1) {
                                setSortType(0);
                              } else if (!sortType) {
                                setSortType(-1);
                              }
                            }
                          : undefined
                      }
                    >
                      {name}{' '}
                      {(
                        asyncPagination
                          ? asyncPagination.sortedColumn?.key === key
                          : sortValue === activeSortValue
                      ) ? (
                        (asyncPagination?.sortedColumn?.sortType ||
                          sortType) === -1 ? (
                          <ArrowDown className="scale-75" />
                        ) : (asyncPagination?.sortedColumn?.sortType ||
                            sortType) === 1 ? (
                          <ArrowUp className="scale-75" />
                        ) : (
                          ''
                        )
                      ) : (
                        ''
                      )}
                    </TableHead>
                  )
              )}
              {isColumnVisibilityEnabled && (
                <Popover>
                  <PopoverTrigger className="flex items-center h-12 ml-auto mr-4">
                    <Columns2Icon className="text-muted-foreground" />
                  </PopoverTrigger>
                  <PopoverContent>
                    <Card className="flex flex-col gap-2 p-4 pr-10">
                      {cols.map(
                        (c) =>
                          c.name !== '' && (
                            <div className="flex gap-2 items-center">
                              <Checkbox
                                disabled={c.alwaysVisible}
                                id={`column-${c.key?.toString()}`}
                                checked={!hiddenColumnsKeys.includes(c.key)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setHiddenColumnsKeys((prev) => [
                                      ...prev.filter(
                                        (vColumnKey) => vColumnKey !== c.key
                                      ),
                                    ]);
                                  } else {
                                    setHiddenColumnsKeys((prev) => [
                                      ...prev,
                                      c.key,
                                    ]);
                                  }
                                }}
                              />
                              <label
                                htmlFor={`column-${c.key?.toString()}`}
                                className="cursor-pointer"
                              >
                                {c.name}
                              </label>
                            </div>
                          )
                      )}
                    </Card>
                  </PopoverContent>
                </Popover>
              )}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {entriesToRender.map((e) => (
            <TableRow 
              key={e.id}
              className={onRowClick ? 'cursor-pointer hover:bg-muted/70' : undefined}
              onClick={onRowClick ? () => onRowClick(e) : undefined}
              onKeyDown={onRowClick ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRowClick(e);
                }
              } : undefined}
              tabIndex={onRowClick ? 0 : undefined}
            >
              {cols.map(
                ({ cell, key }, i) =>
                  !hiddenColumnsKeys.includes(key) && (
                    <TableCell className="first:pl-4 last:pr-4" key={i}>
                      {cell(e)}
                    </TableCell>
                  )
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {children}
      {((asyncPagination?.pageCount || 0) > 1 ||
        (pageSize && entries.length > pageSize)) && (
        <AdvancedTablePagination
          {...(asyncPagination
            ? asyncPagination
            : {
                page,
                setPage,
                pageSize,
                pageCount: Math.ceil(entries.length / pageSize),
              })}
        />
      )}
    </div>
  );
};

interface PaginationProps {
  page: number;
  setPage: (page: number) => void;
  pageCount: number;
  pageSize: number;
}

const AdvancedTablePagination = ({
  page,
  setPage,
  pageCount,
  pageSize,
}: PaginationProps) => {
  useEffect(() => {
    if (pageSize && page > pageCount - 1) {
      setPage(Math.max(pageCount - 1, 0));
    }
  }, [pageCount, page, setPage, pageSize]);

  const getPaginationItem = (pageIndex: number) => {
    const isEdgePage = pageIndex === 0 || pageIndex === pageCount - 1;
    const isNearEdgePage = pageIndex === 1 || pageIndex === pageCount - 2;
    const isNearCurrentPage = Math.abs(page - pageIndex) <= 1;

    if (isEdgePage || isNearEdgePage || isNearCurrentPage || pageCount <= 5) {
      return (
        <Button
          hoverVariant={'destructive'}
          variant={page === pageIndex ? 'default' : 'ghost'}
          key={pageIndex + 1}
          onClick={() => page !== pageIndex && setPage(pageIndex)}
          className={`${
            isNearEdgePage && !isNearCurrentPage && 'hidden lg:flex'
          }`}
        >
          {pageIndex + 1}
        </Button>
      );
    } else if (
      (pageIndex === 2 && page !== 0) ||
      (pageIndex === pageCount - 3 && page !== pageCount - 1)
    ) {
      return <EllipsisIcon size={14} />;
    } else {
      return null;
    }
  };

  return (
    <div className="w-full flex items-center justify-center xs:justify-between mt-4 px-4">
      <Button
        variant="outlineFg"
        size="sm"
        onClick={() => {
          page > 0 && setPage(page - 1);
        }}
        disabled={page === 0}
        className="hidden xs:flex gap-1"
      >
        <ArrowLeft size={16} />
        <span className="hidden sm:block">Previous</span>
      </Button>
      <div className={`flex gap-0.5 justify-center items-center mt-2`}>
        {Array(pageCount)
          .fill('')
          .map((_, i) => getPaginationItem(i))}
      </div>
      <Button
        variant="outlineFg"
        size="sm"
        onClick={() => {
          page < pageCount - 1 && setPage(page + 1);
        }}
        disabled={page === pageCount - 1}
        className="hidden xs:flex gap-1"
      >
        <span className="hidden sm:block">Next</span>
        <ArrowRight size={16} />
      </Button>
    </div>
  );
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
